import { TASK_CALLBACK_STATUS, TASK_PRIORITY, TASK_STEP_TYPE } from '../../constants';
import {
  buildLinkedTaskStructure,
  formatDue,
  getDueTimestamp,
  isAutomaticWorkflowTask,
  isTaskBlockedStatus,
  isTaskEscalatedStatus,
  isTaskReadinessComplete,
  isTaskVisible,
} from '../../helpers';

const OPEN_STATUSES = [0, 1, 2];

export const isOpenStatus = (status) => OPEN_STATUSES.includes(status);

export const formatLeadName = (lead) =>
  [lead?.firstName, lead?.lastName].filter(Boolean).join(' ') || 'Unnamed lead';

const toTimestamp = (value) => {
  if (!value) return Number.POSITIVE_INFINITY;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
};

/**
 * Shape a lead task queue item like a DashboardV2 task so it can be rendered by the
 * same TaskRow and read by the same helpers as the Tasks tab. Lead-specific fields
 * ride along untouched for the lead detail panel.
 */
export const adaptLeadTask = (task, titleOverrides = {}) => {
  if (!task) return null;

  const lead = task.lead || {};
  const stepType =
    `${task.stepType || ''}`.trim().toLowerCase() ||
    (task.taskType === 'auto' ? TASK_STEP_TYPE.Automatic : TASK_STEP_TYPE.Manual);
  const owner = task.assigneeName || (task.assignee ? 'Assigned' : null);
  // The queue reports workflowReady from the task token; the Tasks-tab helpers key off
  // callbackStatus, so surface one as the other when the backend has not stamped it.
  const callbackStatus =
    task.callbackStatus ?? (task.workflowReady ? TASK_CALLBACK_STATUS.Pending : null);
  // The workflow definition keeps decision answers in metadata.dependency, while the
  // linked/parallel prerequisite derived from the workflow graph lives on the column.
  // The Tasks-tab helpers read both off one object, so merge them.
  const metadataDependency =
    task.metadata?.dependency && typeof task.metadata.dependency === 'object'
      ? task.metadata.dependency
      : null;
  const dependency =
    task.dependency || metadataDependency
      ? { ...(metadataDependency || {}), ...(task.dependency || {}) }
      : null;

  return {
    ...task,
    isLeadTask: true,
    title: (task.stateName && titleOverrides[task.stateName]) || task.subject,
    subject: task.subject,
    due: task.dueAt ?? null,
    owner,
    patientId: task.leadResourceId,
    patient: {
      id: task.leadResourceId,
      name: formatLeadName(lead),
      firstName: lead.firstName ?? null,
      lastName: lead.lastName ?? null,
      email: lead.email ?? null,
      phone: lead.phone ?? null,
      status: lead.status ?? null,
      businessUnit: lead.businessUnit ?? null,
      leadSource: lead.leadSource ?? null,
      // Set once the lead has its patient: the detail/modal show the outcome and lock.
      patientId: lead.patientId ?? null,
      consentSentAt: lead.consentSentAt ?? null,
    },
    priority: `${task.priority || ''}`.trim().toLowerCase() || TASK_PRIORITY.Medium,
    team: task.team ?? null,
    stepType,
    isVisible: task.isVisible ?? true,
    callbackStatus,
    taskDescription: task.taskDescription ?? task.metadata?.description ?? null,
    taskSop: task.taskSop ?? task.metadata?.sop_url ?? null,
    dependency,
    parentTaskId: task.parentTaskId ?? null,
    blockedStatus: task.blockedStatus ?? null,
    blockedReason: task.blockedReason ?? null,
    blockedAt: task.blockedAt ?? null,
    escalatedStatus: task.escalatedStatus ?? null,
    escalatedTo: task.escalatedTo ?? null,
    escalatedAt: task.escalatedAt ?? null,
    escalatedReason: task.escalatedReason ?? null,
    workflowInstanceId: task.workflowInstanceId ?? null,
    stateName: task.stateName ?? null,
    externalObjectType: 'lead_resource_id',
    externalObjectId: task.leadResourceId,
  };
};

/**
 * Collapse a flat lead-task list into one entry per lead. Groups keep the order the
 * server returned (soonest due first); tasks inside a lead run in workflow order.
 */
export const groupTasksByLead = (tasks = []) => {
  const byLead = new Map();

  tasks.forEach((task) => {
    const leadId = task?.leadResourceId;
    if (!leadId) return;

    if (!byLead.has(leadId)) {
      byLead.set(leadId, {
        key: `lead-${leadId}`,
        leadResourceId: leadId,
        lead: task.lead,
        tasks: [],
      });
    }
    byLead.get(leadId).tasks.push(task);
  });

  return [...byLead.values()].map((group) => {
    const tasksInWorkflowOrder = [...group.tasks].sort(
      (left, right) => toTimestamp(left.createdAt) - toTimestamp(right.createdAt),
    );
    // Automatic steps are shown for context but are never someone's work, and locked
    // (not-yet-visible) steps cannot be worked yet, so neither counts as open or can be
    // the active task.
    const openTasks = tasksInWorkflowOrder.filter(
      (task) => isOpenStatus(task.status) && !isAutomaticWorkflowTask(task) && isTaskVisible(task),
    );
    const activeTask = openTasks.find((task) => task.workflowReady) || openTasks[0] || null;
    const nextDue = activeTask?.due ?? openTasks.map((task) => task.due).find(Boolean) ?? null;

    return {
      ...group,
      tasks: tasksInWorkflowOrder,
      linkedTaskStructure: buildLinkedTaskStructure(tasksInWorkflowOrder),
      name: formatLeadName(group.lead),
      openCount: openTasks.length,
      doneCount: tasksInWorkflowOrder.filter((task) => isTaskReadinessComplete(task)).length,
      criticalCount: openTasks.filter((task) => task.priority === TASK_PRIORITY.Critical).length,
      blockedCount: openTasks.filter((task) => isTaskBlockedStatus(task)).length,
      escalatedCount: openTasks.filter((task) => isTaskEscalatedStatus(task)).length,
      activeTask,
      nextDueLabel: nextDue ? formatDue(nextDue).text : '—',
      nextDueTimestamp: nextDue ? getDueTimestamp({ due: nextDue }) : Number.POSITIVE_INFINITY,
    };
  });
};

export const matchesLeadSearch = (task, query) => {
  const normalized = `${query || ''}`.trim().toLowerCase();
  if (!normalized) return true;

  return [
    task?.patient?.name,
    task?.patient?.email,
    task?.patient?.phone,
    task?.title,
    task?.subject,
  ].some((value) => `${value || ''}`.toLowerCase().includes(normalized));
};

export const matchesLeadOwner = (task, owner, currentUserId) => {
  if (owner === 'my') return `${task?.assignee ?? ''}` === `${currentUserId ?? ''}`;
  if (owner === 'unassigned') return task?.assignee == null && !isAutomaticWorkflowTask(task);
  return true;
};
